import { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AlertDialogComponent, DataTable, Pagination } from "@/components/shared";
import { getColumnsHoliday } from "./columns";
import { Holiday, HolidayEdge, PaginatedHolidaysQuery, PaginatedHolidaysQueryVariables, PaginatedHolidaysDocument } from "@/graphql/__generated__/graphql";
import HolidaysFilter from "./HolidaysFilter";
import { useRemoveHolidayMutationHook } from "@/hooks";
import useUserStore from "@/store/shield";
import AddHolidayModal from "./AddHolidayModal";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedHolidaysQueryVariables>) => void;
}

/**
 * HolidayComponent component displays a table of holidays across German provinces.
 * It allows filtering and sorting of holidays, and provides a modal for adding new holidays.
 * It also allows removing holidays.
 *
 * @component
 * @example
 * <HolidayComponent />
 */
function HolidayComponent() {

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components", "common"]);
  const { removeHolidayMutation } = useRemoveHolidayMutationHook();

  const { isConfigAdmin } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedHolidays";

  // State management
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveHolidayOpen, setIsRemoveHolidayOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);

  const onFilterSubmit = (variables: PaginatedHolidaysQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  const handleRemoveClick = (holiday: Holiday) => {
    setSelectedHoliday(holiday);
    setIsRemoveHolidayOpen(true);
  };

  const handleRemoveHoliday = (holiday: Holiday) => {
    removeHolidayMutation({ variables: { removeHolidayId: holiday.id } }).finally(() => {
      setIsRemoveHolidayOpen(false);
    });
  };

  const columns = useMemo(() => getColumnsHoliday(intl, t, handleRemoveClick, isConfigAdmin), [intl, t, handleRemoveClick, isConfigAdmin]);

  return (
    <>
      <Pagination<HolidayEdge, PaginatedHolidaysQuery, PaginatedHolidaysQueryVariables>
        ref={paginationRef} props={{ queryDocument: PaginatedHolidaysDocument, paginationName }}>
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 justify-end">
              <HolidaysFilter onFilterSubmit={onFilterSubmit} displayAmount={displayAmount as number} />
              {isConfigAdmin && <Button
                onClick={() => setIsAddModalOpen(true)}
              >
                {t("common:buttons.add")}
              </Button>}
            </div>
            <DataTable
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map(edge => edge.node) ?? []}
              columns={columns}
              enableColumnPinning
              columnPinning={{
                right: ["actions"]
              }}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>

      {/* Add Holiday Modal */}
      <AddHolidayModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
      />

      {/* Remove Holiday Alert Dialog */}
      <AlertDialogComponent
        open={isRemoveHolidayOpen}
        onOpenChange={setIsRemoveHolidayOpen}
        actionButtonFn={() => handleRemoveHoliday(selectedHoliday as Holiday)}
        title={t("components:alertdialogs.removetitle")}
        bodyText={t("components:alertdialogs.removeholiday")}
      />
    </>
  );
}

export default HolidayComponent;
